import { ResponsiveBar } from "@nivo/bar";
import { ResponsiveLine } from "@nivo/line";
import { ResponsivePie } from "@nivo/pie";
import { useGetList } from "ra-core";
import { useMemo } from "react";

import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Contact, Deal } from "../types";
import { runReport, type ChartType, type ReportConfig } from "./ReportBuilder";
import { useReportFieldDefs } from "./useReportFields";

/**
 * Renders a ReportConfig in any chart type - the single renderer behind the
 * report builder live preview and every dashboard tile.
 *
 * Palette note: colors come from the validated Obvious chart family
 * (#2e6ba9 lead + the muted channel colors already used product-wide).
 * Run the palette validator before changing them.
 */

export const CHART_PALETTE = [
  "#2e6ba9",
  "#b07d3f",
  "#8fb586",
  "#c97a63",
  "#9db4cc",
  "#d9b36b",
  "#94a3b8",
];

const MAX_DONUT_SLICES = 6;

/** The chart type actually rendered when config.chartType is unset. */
export const effectiveChartType = (
  config: ReportConfig,
  isTimeSeries: boolean,
): ChartType => {
  if (config.chartType) return config.chartType;
  if (!config.groupBy) return "stat";
  return isTimeSeries ? "column" : "bar";
};

const nivoTheme = (small: boolean) => ({
  text: { fontSize: small ? 10 : 11, fill: "var(--muted-foreground)" },
  grid: { line: { stroke: "var(--border)", strokeWidth: 1 } },
  tooltip: {
    container: { background: "transparent", boxShadow: "none", padding: 0 },
  },
});

const Tip = ({ label, value }: { label: string; value: string }) => (
  <div className="p-2 bg-secondary rounded shadow inline-flex items-center gap-1 text-secondary-foreground text-sm">
    <strong>{label}:</strong> {value}
  </div>
);

export const ReportWidget = ({
  config,
  height = 240,
}: {
  config: ReportConfig;
  height?: number;
}) => {
  const { currency, lifecycleStages } = useConfigurationContext();
  const { data: deals = [] } = useGetList<Deal>("deals", {
    pagination: { page: 1, perPage: 1000 },
  });
  const { data: contacts = [] } = useGetList<Contact>("contacts", {
    pagination: { page: 1, perPage: 1000 },
  });
  const fields = useReportFieldDefs(config.object);
  const records = config.object === "deals" ? deals : contacts;
  const result = useMemo(
    () => runReport(records, config, fields),
    [records, config, fields],
  );

  const fmt = (n: number) =>
    config.measure === "count"
      ? n.toLocaleString("en-US")
      : n.toLocaleString("en-US", {
          notation: "compact",
          style: "currency",
          currency,
          currencyDisplay: "narrowSymbol",
          maximumFractionDigits: 1,
        });

  const isTimeSeries =
    fields.find((f) => f.key === config.groupBy)?.type === "date";
  const type = effectiveChartType(config, isTimeSeries);
  const small = height <= 260;
  const groups = result.groups ?? [];

  // --- Stat (works grouped or not: shows the overall total) ---------------
  if (type === "stat" || !result.groups) {
    return (
      <div className="flex flex-col justify-center h-full min-h-24">
        <p className="text-3xl font-semibold tabular-nums">
          {fmt(result.total ?? 0)}
        </p>
        <p className="text-xs text-muted-foreground tabular-nums">
          {result.rows.length} records
        </p>
      </div>
    );
  }

  if (groups.length === 0) {
    return <p className="text-sm text-muted-foreground">No matching records.</p>;
  }

  // --- Table --------------------------------------------------------------
  if (type === "table") {
    return (
      <div className="overflow-y-auto" style={{ maxHeight: height }}>
        <table className="text-sm w-full">
          <thead className="sticky top-0 bg-card">
            <tr className="text-left text-muted-foreground">
              <th className="py-1 pr-4 font-medium">Group</th>
              <th className="py-1 pr-4 font-medium">Value</th>
              <th className="py-1 font-medium">Records</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <tr key={g.label} className="border-t border-border">
                <td className="py-1 pr-4">{g.label}</td>
                <td className="py-1 pr-4 tabular-nums">{fmt(g.value)}</td>
                <td className="py-1 tabular-nums">{g.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // --- Funnel -------------------------------------------------------------
  if (type === "funnel") {
    // Order by the tenant's lifecycle ladder when grouping by it; otherwise
    // keep descending value order so the funnel narrows naturally.
    const ladder = lifecycleStages.map((s) => s.label);
    const ordered =
      config.groupBy === "lifecycle_stage"
        ? [...groups].sort(
            (a, b) => ladder.indexOf(a.label) - ladder.indexOf(b.label),
          )
        : [...groups].sort((a, b) => b.value - a.value);
    const max = Math.max(...ordered.map((g) => g.value), 1);
    return (
      <div
        className="flex flex-col justify-center gap-1 overflow-y-auto"
        style={{ maxHeight: height }}
      >
        {ordered.map((g, i) => {
          const prev = i > 0 ? ordered[i - 1]!.value : null;
          const conv =
            prev && prev > 0 ? Math.round((g.value / prev) * 100) : null;
          return (
            <div key={g.label} className="flex items-center gap-2">
              <span className="w-24 shrink-0 text-xs text-muted-foreground truncate text-right">
                {g.label}
              </span>
              <div className="flex-1 flex justify-center">
                <div
                  className="h-6 rounded-sm flex items-center justify-center text-[11px] font-medium text-white tabular-nums min-w-10"
                  style={{
                    width: `${Math.max((g.value / max) * 100, 8)}%`,
                    backgroundColor: CHART_PALETTE[0],
                    opacity: 1 - i * 0.09,
                  }}
                  title={`${g.label}: ${fmt(g.value)}`}
                >
                  {fmt(g.value)}
                </div>
              </div>
              <span className="w-12 shrink-0 text-[11px] text-muted-foreground tabular-nums">
                {conv != null ? `${conv}%` : ""}
              </span>
            </div>
          );
        })}
        <p className="text-[10px] text-muted-foreground text-right pr-1">
          % = conversion from the stage above
        </p>
      </div>
    );
  }

  // --- Donut --------------------------------------------------------------
  if (type === "donut") {
    const top = [...groups].sort((a, b) => b.value - a.value);
    const shown = top.slice(0, MAX_DONUT_SLICES);
    const rest = top.slice(MAX_DONUT_SLICES);
    const data = [
      ...shown.map((g) => ({ id: g.label, value: g.value })),
      ...(rest.length
        ? [{ id: "Other", value: rest.reduce((s, g) => s + g.value, 0) }]
        : []),
    ];
    const total = data.reduce((s, d) => s + d.value, 0);
    return (
      <div className="flex items-center gap-4" style={{ height }}>
        <div className="h-full flex-1 min-w-0">
          <ResponsivePie
            data={data}
            colors={CHART_PALETTE}
            innerRadius={0.62}
            padAngle={1}
            cornerRadius={2}
            margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
            enableArcLabels={false}
            enableArcLinkLabels={false}
            theme={nivoTheme(small)}
            tooltip={({ datum }: any) => (
              <Tip label={String(datum.id)} value={fmt(datum.value)} />
            )}
          />
        </div>
        <div className="flex flex-col gap-1 pr-2 max-w-[45%]">
          {data.map((d, i) => (
            <div key={d.id} className="flex items-center gap-1.5 text-xs">
              <span
                className="w-2.5 h-2.5 rounded-full shrink-0"
                style={{ background: CHART_PALETTE[i % CHART_PALETTE.length] }}
              />
              <span className="truncate">{d.id}</span>
              <span className="text-muted-foreground tabular-nums ml-auto pl-2">
                {total ? Math.round((d.value / total) * 100) : 0}%
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // --- Line / Area --------------------------------------------------------
  if (type === "line" || type === "area") {
    const ordered = isTimeSeries
      ? groups
      : [...groups].sort((a, b) => a.label.localeCompare(b.label));
    return (
      <div style={{ height }}>
        <ResponsiveLine
          data={[
            {
              id: "Value",
              data: ordered.map((g) => ({ x: g.label, y: g.value })),
            },
          ]}
          colors={[CHART_PALETTE[0]!]}
          margin={{
            top: 12,
            right: 16,
            bottom: small ? 32 : 42,
            left: small ? 56 : 64,
          }}
          curve="monotoneX"
          enableArea={type === "area"}
          areaOpacity={0.15}
          lineWidth={2}
          pointSize={6}
          pointColor={CHART_PALETTE[0]}
          enableGridX={false}
          useMesh
          theme={nivoTheme(small)}
          yScale={{ type: "linear", min: 0, max: "auto" }}
          axisLeft={{
            tickSize: 0,
            tickPadding: 6,
            format: (v: number) => fmt(v),
            tickValues: small ? 4 : 5,
          }}
          axisBottom={{
            tickSize: 0,
            tickPadding: 6,
            tickRotation: ordered.length > (small ? 6 : 10) ? -35 : 0,
          }}
          tooltip={({ point }: any) => (
            <Tip label={String(point.data.x)} value={fmt(Number(point.data.y))} />
          )}
        />
      </div>
    );
  }

  // --- Column (vertical) / Bar (horizontal) -------------------------------
  const horizontal = type === "bar";
  const data = groups.map((g) => ({ label: g.label, Value: g.value }));
  return (
    <div style={{ height }}>
      <ResponsiveBar
        data={horizontal ? [...data].reverse() : data}
        indexBy="label"
        keys={["Value"]}
        layout={horizontal ? "horizontal" : "vertical"}
        colors={[CHART_PALETTE[0]!]}
        margin={{
          top: 4,
          right: 16,
          bottom: small ? 32 : 42,
          left: horizontal ? (small ? 110 : 130) : small ? 56 : 64,
        }}
        padding={0.35}
        borderRadius={3}
        enableLabel={false}
        theme={nivoTheme(small)}
        axisLeft={{
          tickSize: 0,
          tickPadding: 6,
          ...(horizontal
            ? {}
            : { format: (v: number) => fmt(v), tickValues: small ? 4 : 5 }),
        }}
        axisBottom={{
          tickSize: 0,
          tickPadding: 6,
          ...(horizontal
            ? { format: (v: number) => fmt(v), tickValues: small ? 4 : 5 }
            : {}),
        }}
        tooltip={({ value, indexValue }: any) => (
          <Tip label={String(indexValue)} value={fmt(value)} />
        )}
      />
    </div>
  );
};
